import React, { useEffect, useState, useRef } from 'react';
import { Card, Col, Row, Skeleton } from 'antd';
import HotelResultFilter from './HotelResultFilter';
import SearchBarResultHotel from './SearchBarResultHotel';
import { useSelector } from 'react-redux';
import HotelCard from './HotelCard';
import { no_data_found } from '../../../utils/ImagePath';
import { FaArrowUp, FaChevronUp } from 'react-icons/fa';
import HotelCityResultContent from './HotelCityResultContent';
import Seo from '../../helmet/Helmet';

const HotelResult = () => {
    const allHotelList = useSelector(state => state.hotelData.allHotelList);
    const loadingAllHotelList = useSelector(state => state.hotelData.loading);
    const hotelFilter = useSelector(state => state.hotelFilter.loading);
    const filteredData = useSelector(state => state?.hotelFilter?.filteredData);
    const { data, loading } = useSelector((state) => state.seoHotelCity);

    const [visibleHotels, setVisibleHotels] = useState([]);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const itemsPerPage = 15;
    const loaderRef = useRef(null);

    const hotelsToDisplay = (filteredData?.hotels || []).filter(
        (hotel) =>
            hotel.name !== "Unnamed Hotel" && hotel.rt !== "N/A"
    );

    const hotelSearchKey = JSON.parse(localStorage.getItem("hotelSearchKey"))
    if (hotelSearchKey?.source == 'hotel') {
        const matchedHotelIndex = hotelsToDisplay.findIndex(
            (hotel) => {
                return hotel.name.trim().toLowerCase() === hotelSearchKey.name.trim().toLowerCase();
            }
        );
        if (matchedHotelIndex > -1) {
            const [matchedHotel] = hotelsToDisplay.splice(matchedHotelIndex, 1);
            hotelsToDisplay.unshift(matchedHotel);
        }
    }

    useEffect(() => {
        let timer;
        if (loading) {
            setIsLoading(true);
        } else {
            timer = setTimeout(() => setIsLoading(false), 200);
        }
        return () => clearTimeout(timer);
    }, [loading]);

    useEffect(() => {
        const nextHotels = hotelsToDisplay.slice(0, page * itemsPerPage);
        setVisibleHotels(nextHotels);
    }, [page, hotelsToDisplay]);


    useEffect(() => {
        if (!loaderRef.current || hotelsToDisplay.length <= visibleHotels.length) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setPage((prev) => prev + 1);
                }
            },
            { threshold: 1 }
        );
        observer.observe(loaderRef.current);
        return () => {
            if (loaderRef.current) observer.unobserve(loaderRef.current);
        };
    }, [loaderRef, visibleHotels.length, hotelsToDisplay.length]);


    const fetchRoomDetails = (item) => {
        const selectedRoomDetails = {
            id: item?.id,
            traceId: item?.traceId,
            resultIndex: item?.resultIndex,
            uid: item?.uid,
            vendor: item?.vendor
        };
        const params = new URLSearchParams();
        if (selectedRoomDetails?.id) params.append("id", selectedRoomDetails?.id);
        if (selectedRoomDetails?.traceId) params.append("TraceId", selectedRoomDetails?.traceId);
        if (selectedRoomDetails?.resultIndex) params.append("ResultIndex", selectedRoomDetails?.resultIndex);
        if (selectedRoomDetails?.uid) params.append("HotelCode", selectedRoomDetails?.uid);
        if (selectedRoomDetails?.vendor) params.append("vendor", selectedRoomDetails?.vendor);
        const url = `/hotel-details?${params.toString()}`;
        const newTab = window.open(url, "_blank");
        if (newTab) {
            localStorage.setItem("selectedRoomDetails", JSON.stringify(selectedRoomDetails));
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <>
            <Seo
                title={data?.meta_title}
                description={data?.meta_description}
                link={window.location.href}
                keywords={data?.meta_keywords}
                largeTwitterCard={true}
                addPostfixTitle={false}
                noIndex={true}
            />

            <SearchBarResultHotel />

            <section className="container hotel_result pt-0">
                <Row gutter={16} className="mobile_margin">
                    <Col lg={6} md={9} xs={24} className="mobile_padding">
                        <Skeleton
                            className="p-3 mb-3 mb-md-4"
                            active
                            paragraph={{ rows: 14 }}
                            loading={loadingAllHotelList || isLoading}
                            style={{ background: "#ffff", borderRadius: "12px" }}
                        >
                            <div className='d-none d-md-block position-sticky' style={{ top: '190px' }}>
                                <HotelResultFilter hotelsToDisplay={hotelsToDisplay} />

                                {/* Scroll to top  button in desktop/laptop */}
                                <div className='scroll_top'> <button onClick={scrollToTop} aria-label="Scroll to top">Scroll to Top <FaChevronUp /></button> </div>
                            </div>

                        </Skeleton>
                    </Col>

                    <Col lg={18} md={15} xs={24} className="mobile_padding">
                        <Skeleton
                            paragraph={{ rows: 4 }}
                            loading={loadingAllHotelList || hotelFilter || isLoading}
                            style={{ background: "#fff", borderRadius: "0.3rem" }}
                            className="p-3 mb-1"
                            active
                        >
                            {visibleHotels.length === 0 ? (
                                <Card loading={loadingAllHotelList || hotelFilter} className="p-4">
                                    <img
                                        loading="lazy"
                                        src={no_data_found}
                                        alt="No Data Found"
                                        className="w-100"
                                        style={{ height: "320px" }}
                                    />
                                </Card>
                            ) : (
                                <>
                                    <div className='mb-2 d-md-block d-none'>
                                        <h1 className='d-flex align-items-center'>Best Hotels in {hotelSearchKey?.name?.trim()} – From Budget to Luxury</h1>
                                    </div>
                                    {visibleHotels.map((item, index) => (
                                        <HotelCard key={index} item={item} isLoading={isLoading} fetchRoomDetails={fetchRoomDetails} />
                                    ))}
                                    <div ref={loaderRef} style={{ height: "80px", textAlign: "center", paddingTop: "1rem" }}>
                                        {visibleHotels.length < hotelsToDisplay.length ? (
                                            <span>Loading more hotels...</span>
                                        ) : (
                                            <span>That's all the options we've got</span>
                                        )}
                                    </div>

                                </>
                            )}
                        </Skeleton>
                        <HotelCityResultContent />
                    </Col>
                </Row>
            </section>

            {/* Scroll to top  button in mobile view */}
            <div className='scroll_top-2'><button onClick={scrollToTop} aria-label="scroll to top"><FaArrowUp /></button></div>
        </>
    );
};

export default HotelResult;